import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    DetailsList,
    DetailsListLayoutMode,
    SelectionMode,
    CommandBar,
    Stack,
    Text,
    TextField,
    DatePicker,
    Dropdown,
    Panel,
    PanelType,
    Dialog,
    DialogType,
    DialogFooter,
    PrimaryButton,
    DefaultButton,
    Spinner,
    MessageBar,
    MessageBarType,
} from '@fluentui/react';

import {
    useAdminApi,
    apiErrorMessage,
    MANUAL_PLATFORMS,
    PLATFORM_LABELS,
    platformLabel,
    toDateString,
    formatDate,
    formatDay,
    formatDateTime,
    formatNumber,
    formatMoney,
    daysBefore,
    daysBetween,
    downloadCsv,
    parseCsv,
    homeStyles,
    sectionStyles,
    statStyles,
    mutedText,
} from '../common';

// ค่าโฆษณารายวัน (GET / PUT /marketing/spend) Google Ads ดึงจาก API ทุกเช้า แพลตฟอร์มอื่นกรอกเองหรืออัปโหลด CSV
// ต้นทุนผลิต content (ไม่ใช่โฆษณา) กรอกแยก (type 'content-cost') ไม่นับรวมในค่าโฆษณา / ROAS ของโฆษณา

const MAX_DAYS = 366;
const SYNC_MAX_DAYS = 90;
const BATCH_SIZE = 1000;

const SOURCE_LABELS = {
    api: 'API',
    manual: 'กรอกเอง',
    csv: 'CSV',
};

const CSV_HEADER = ['date', 'platform', 'platform_campaign_id', 'campaign_name', 'cost', 'impressions', 'clicks'];

const MANUAL_PLATFORM_OPTIONS = MANUAL_PLATFORMS.map((key) => ({ key, text: PLATFORM_LABELS[key] }));

const EMPTY_ENTRY = {
    platform: 'meta',
    date: null,
    campaignId: '',
    platformCampaignId: '',
    platformCampaignName: '',
    cost: '',
    impressions: '',
    clicks: '',
};

const EMPTY_CONTENT_COST = {
    campaignId: '',
    date: null,
    cost: '',
    note: '',
};

// แถว CSV → รายการค่าโฆษณา คืน { items, errors }
function itemsFromCsv(text) {
    const rows = parseCsv(text);
    if (rows.length < 2) return { items: [], errors: ['ไม่มีข้อมูลในไฟล์'] };

    const header = rows[0].map((name) => name.toLowerCase());
    const missing = ['date', 'platform', 'platform_campaign_id', 'cost'].filter((name) => !header.includes(name));
    if (missing.length > 0) return { items: [], errors: ['ไม่มีคอลัมน์ ' + missing.join(', ')] };

    const errors = [];
    const items = rows.slice(1).map((values, index) => {
        const value = (name) => {
            const position = header.indexOf(name);
            return position >= 0 ? values[position] || '' : '';
        };
        const item = {
            date: value('date'),
            platform: value('platform').toLowerCase(),
            platformCampaignId: value('platform_campaign_id'),
            platformCampaignName: value('campaign_name'),
            cost: value('cost').replace(/,/g, ''),
            impressions: value('impressions').replace(/,/g, ''),
            clicks: value('clicks').replace(/,/g, ''),
        };

        if (!/^\d{4}-\d{2}-\d{2}$/.test(item.date)) errors.push(`แถว ${index + 2}: date ต้องเป็น YYYY-MM-DD (ค.ศ.)`);
        if (!MANUAL_PLATFORMS.includes(item.platform)) errors.push(`แถว ${index + 2}: platform ต้องเป็น ${MANUAL_PLATFORMS.join(' / ')}`);

        return item;
    });

    return { items, errors };
}

const Spend = () => {

    const request = useAdminApi();
    const fileInput = useRef(null);

    const today = new Date();
    const yesterday = daysBefore(today, 1);

    const [fromDate, setFromDate] = useState(daysBefore(today, 30));
    const [toDate, setToDate] = useState(yesterday);
    const [data, setData] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState(null);
    const [refresh, setRefresh] = useState(0);
    const [platformFilter, setPlatformFilter] = useState('all');

    const [entry, setEntry] = useState(null);
    const [entryError, setEntryError] = useState('');
    const [contentEntry, setContentEntry] = useState(null);
    const [contentError, setContentError] = useState('');
    const [csv, setCsv] = useState(null);
    const [sync, setSync] = useState(null);
    const [busy, setBusy] = useState(false);

    const from = toDateString(fromDate);
    const to = toDateString(toDate);

    useEffect(() => {
        if (daysBetween(fromDate, toDate) > MAX_DAYS) {
            setError(`เลือกช่วงได้ไม่เกิน ${MAX_DAYS} วัน`);
            setData(null);
            return;
        }

        setLoading(true);
        setError('');

        Promise.all([
            request('get', '/marketing/spend?from=' + from + '&to=' + to),
            request('get', '/marketing/campaigns'),
        ])
            .then(([spendResponse, campaignsResponse]) => {
                setData(spendResponse.data.data);
                setCampaigns(campaignsResponse.data.data.campaigns);
            })
            .catch((err) => {
                console.log('Spend error: ', err);
                setError('โหลดค่าโฆษณาไม่สำเร็จ');
                setData(null);
            })
            .finally(() => setLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request, from, to, refresh]);

    const campaignById = useMemo(() => new Map(campaigns.map((item) => [item.id, item])), [campaigns]);

    const spend = useMemo(() => {
        if (!data) return [];
        return data.spend.filter((item) => platformFilter === 'all' || item.platform === platformFilter);
    }, [data, platformFilter]);

    const byPlatform = useMemo(() => {
        const totals = new Map();
        for (const item of data ? data.spend : []) {
            totals.set(item.platform, (totals.get(item.platform) || 0) + (Number(item.cost) || 0));
        }
        return [...totals.entries()].sort((a, b) => b[1] - a[1]);
    }, [data]);

    const googleSync = data ? data.sync.find((item) => item.platform === 'google-ads') : null;

    const campaignName = (item) => {
        const registered = item.campaignId ? campaignById.get(item.campaignId) : null;
        return registered ? registered.name : item.platformCampaignName || item.platformCampaignId;
    };

    // ส่งค่าโฆษณาทีละไม่เกิน 1000 รายการ
    const saveItems = async (items, source) => {
        let saved = 0;
        for (let i = 0; i < items.length; i += BATCH_SIZE) {
            const response = await request('put', '/marketing/spend', { source, items: items.slice(i, i + BATCH_SIZE) });
            saved += response.data.data.saved;
        }
        return saved;
    };

    const saveEntry = () => {
        const item = {
            platform: entry.platform,
            date: entry.date ? toDateString(entry.date) : '',
            campaignId: entry.campaignId || null,
            platformCampaignId: entry.platformCampaignId.trim() || null,
            platformCampaignName: entry.platformCampaignName.trim() || null,
            cost: entry.cost,
            impressions: entry.impressions,
            clicks: entry.clicks,
        };

        setBusy(true);
        setEntryError('');

        saveItems([item], 'manual')
            .then(() => {
                setNotice({ type: MessageBarType.success, text: `บันทึกค่าโฆษณา ${platformLabel(item.platform)} วันที่ ${formatDay(item.date)} แล้ว` });
                setEntry(null);
                setRefresh((value) => value + 1);
            })
            .catch((err) => {
                console.log('Spend save error: ', err);
                setEntryError(apiErrorMessage(err, 'บันทึกไม่สำเร็จ'));
            })
            .finally(() => setBusy(false));
    };

    const saveContentCost = () => {
        const item = {
            campaignId: contentEntry.campaignId,
            date: contentEntry.date ? toDateString(contentEntry.date) : '',
            cost: contentEntry.cost,
            note: contentEntry.note.trim(),
        };

        setBusy(true);
        setContentError('');

        request('put', '/marketing/spend', { type: 'content-cost', items: [item] })
            .then(() => {
                setNotice({ type: MessageBarType.success, text: `บันทึกต้นทุน content "${campaignById.get(item.campaignId).name}" วันที่ ${formatDay(item.date)} แล้ว` });
                setContentEntry(null);
                setRefresh((value) => value + 1);
            })
            .catch((err) => {
                console.log('Content cost save error: ', err);
                setContentError(apiErrorMessage(err, 'บันทึกไม่สำเร็จ'));
            })
            .finally(() => setBusy(false));
    };

    const readCsv = (event) => {
        const file = event.target.files[0];
        event.target.value = '';
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => setCsv({ name: file.name, ...itemsFromCsv(String(reader.result)) });
        reader.readAsText(file, 'utf-8');
    };

    const saveCsv = () => {
        setBusy(true);

        saveItems(csv.items, 'csv')
            .then((saved) => {
                setNotice({ type: MessageBarType.success, text: `นำเข้า ${formatNumber(saved)} รายการจาก ${csv.name} แล้ว` });
                setCsv(null);
                setRefresh((value) => value + 1);
            })
            .catch((err) => {
                console.log('CSV save error: ', err);
                const errors = err.response && err.response.data && err.response.data.errors;
                setCsv({ ...csv, errors: errors || [apiErrorMessage(err, 'นำเข้าไม่สำเร็จ')] });
            })
            .finally(() => setBusy(false));
    };

    const runSync = () => {
        const syncFrom = toDateString(sync.from);
        const syncTo = toDateString(sync.to);

        setBusy(true);

        request('post', '/marketing/google-ads/sync?from=' + syncFrom + '&to=' + syncTo)
            .then((response) => {
                setNotice({ type: MessageBarType.success, text: `ดึง Google Ads ${formatDay(syncFrom)} – ${formatDay(syncTo)} แล้ว (${response.data.data.message})` });
            })
            .catch((err) => {
                console.log('Google Ads sync error: ', err);
                // 502 = Google Ads ตอบผิดพลาด ข้อความจาก backend บอกสาเหตุ (เช่น ยังไม่ได้ตั้งค่า)
                const message = err.response && err.response.status === 502 && err.response.data && err.response.data.message;
                setNotice({ type: MessageBarType.error, text: 'ดึง Google Ads ไม่สำเร็จ: ' + (message || apiErrorMessage(err, 'เกิดข้อผิดพลาด')) });
            })
            .finally(() => {
                setBusy(false);
                setSync(null);
                setRefresh((value) => value + 1);
            });
    };

    const csvTotal = csv ? csv.items.reduce((sum, item) => sum + (Number(item.cost) || 0), 0) : 0;
    const syncDays = sync ? daysBetween(sync.from, sync.to) : 0;

    const columns = [
        { key: 'date', name: 'วันที่', minWidth: 100, maxWidth: 120, onRender: (item) => <Text>{formatDay(item.date)}</Text> },
        { key: 'platform', name: 'แพลตฟอร์ม', minWidth: 110, maxWidth: 160, onRender: (item) => <Text>{platformLabel(item.platform)}</Text> },
        { key: 'campaign', name: 'แคมเปญ', minWidth: 200, maxWidth: 300, isResizable: true, onRender: (item) => <Text>{campaignName(item)}</Text> },
        { key: 'registered', name: 'ทะเบียน', minWidth: 90, maxWidth: 110, onRender: (item) => <Text styles={item.campaignId ? undefined : { root: { color: '#a4262c' } }}>{item.campaignId ? 'ผูกแล้ว' : 'ยังไม่ผูก'}</Text> },
        { key: 'cost', name: 'ค่าใช้จ่าย', minWidth: 100, maxWidth: 120, onRender: (item) => <Text>{formatMoney(item.cost)}</Text> },
        { key: 'impressions', name: 'impressions', minWidth: 90, maxWidth: 110, onRender: (item) => <Text>{formatNumber(item.impressions)}</Text> },
        { key: 'clicks', name: 'clicks', minWidth: 70, maxWidth: 90, onRender: (item) => <Text>{formatNumber(item.clicks)}</Text> },
        { key: 'source', name: 'ที่มา', minWidth: 70, maxWidth: 90, onRender: (item) => <Text>{SOURCE_LABELS[item.source] || item.source}</Text> },
        { key: 'syncedTime', name: 'อัปเดต', minWidth: 120, maxWidth: 140, onRender: (item) => <Text>{formatDateTime(item.syncedTime)}</Text> },
    ];

    const contentCost = data ? data.contentCost || [] : [];
    const contentCampaigns = campaigns.filter((item) => item.kind === 'content');

    const contentColumns = [
        { key: 'date', name: 'วันที่', minWidth: 100, maxWidth: 120, onRender: (item) => <Text>{formatDay(item.date)}</Text> },
        { key: 'campaign', name: 'แคมเปญ content', minWidth: 200, maxWidth: 300, isResizable: true, onRender: (item) => <Text>{campaignById.has(item.campaignId) ? campaignById.get(item.campaignId).name : item.campaignId}</Text> },
        { key: 'cost', name: 'ต้นทุน', minWidth: 100, maxWidth: 120, onRender: (item) => <Text>{formatMoney(item.cost)}</Text> },
        { key: 'note', name: 'หมายเหตุ', fieldName: 'note', minWidth: 200, maxWidth: 400, isResizable: true },
        { key: 'syncedTime', name: 'อัปเดต', minWidth: 120, maxWidth: 140, onRender: (item) => <Text>{formatDateTime(item.syncedTime)}</Text> },
    ];

    const commandBarItems = [
        {
            key: 'refresh',
            text: 'รีเฟรช',
            iconProps: { iconName: 'Refresh' },
            disabled: loading,
            onClick: () => setRefresh(refresh + 1),
        },
        {
            key: 'sync',
            text: 'ดึง Google Ads ย้อนหลัง',
            iconProps: { iconName: 'CloudDownload' },
            disabled: loading || busy,
            onClick: () => setSync({ from: daysBefore(yesterday, SYNC_MAX_DAYS - 1), to: yesterday }),
        },
        {
            key: 'manual',
            text: 'กรอกเอง',
            iconProps: { iconName: 'Edit' },
            disabled: loading || busy,
            onClick: () => {
                setEntry({ ...EMPTY_ENTRY, date: yesterday });
                setEntryError('');
            },
        },
        {
            key: 'content',
            text: 'ต้นทุน content',
            iconProps: { iconName: 'Video' },
            disabled: loading || busy || contentCampaigns.length === 0,
            title: contentCampaigns.length === 0 ? 'ลงทะเบียนแคมเปญ content ก่อน' : undefined,
            onClick: () => {
                setContentEntry({ ...EMPTY_CONTENT_COST, date: yesterday });
                setContentError('');
            },
        },
        {
            key: 'upload',
            text: 'อัปโหลด CSV',
            iconProps: { iconName: 'Upload' },
            disabled: loading || busy,
            onClick: () => fileInput.current && fileInput.current.click(),
        },
        {
            key: 'template',
            text: 'แม่แบบ CSV',
            iconProps: { iconName: 'Download' },
            onClick: () => downloadCsv('ad-spend-template.csv', CSV_HEADER, [[toDateString(yesterday), 'meta', '120200000000000000', 'ชื่อแคมเปญ', '500.00', '12000', '150']]),
        },
        {
            key: 'csv',
            text: 'ดาวน์โหลด CSV',
            iconProps: { iconName: 'Download' },
            disabled: loading || spend.length === 0,
            onClick: () => downloadCsv(`ad-spend-${from}-${to}.csv`, [...CSV_HEADER, 'campaign', 'source'],
                spend.map((item) => [item.date, item.platform, item.platformCampaignId, item.platformCampaignName, item.cost, item.impressions, item.clicks, campaignName(item), item.source])),
        },
    ];

    const entryCampaignOptions = entry
        ? [{ key: '', text: '(ไม่เลือก ใส่ campaign ID แทน)' }, ...campaigns.filter((item) => item.platform === entry.platform).map((item) => ({ key: item.id, text: item.name }))]
        : [];

    return (
        <div style={homeStyles}>
            <input type="file" accept=".csv,text/csv" ref={fileInput} style={{ display: 'none' }} onChange={readCsv} />

            <Stack tokens={{ childrenGap: 20 }} styles={{ root: { padding: '2%' } }}>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="end" wrap tokens={{ childrenGap: 10 }}>
                    <Stack>
                        <Text variant="xxLarge" styles={{ root: { fontWeight: 600 } }}>ค่าโฆษณา</Text>
                        <Text styles={mutedText}>
                            Google Ads ดึงอัตโนมัติทุกวัน 06:00 (3 วันล่าสุด) แพลตฟอร์มอื่นกรอกเองหรืออัปโหลด CSV รายการวันและแคมเปญเดิมจะถูกเขียนทับ
                        </Text>
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="end">
                        <DatePicker label="ตั้งแต่" value={fromDate} maxDate={toDate} formatDate={formatDate}
                            onSelectDate={(date) => date && setFromDate(date)} styles={{ root: { width: 160 } }} />
                        <DatePicker label="ถึง" value={toDate} minDate={fromDate} maxDate={today} formatDate={formatDate}
                            onSelectDate={(date) => date && setToDate(date)} styles={{ root: { width: 160 } }} />
                    </Stack>
                </Stack>

                <CommandBar items={commandBarItems} styles={{ root: { padding: 0, background: 'transparent' } }} />

                {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
                {notice && <MessageBar messageBarType={notice.type} onDismiss={() => setNotice(null)}>{notice.text}</MessageBar>}
                {busy && <Spinner label="กำลังทำงาน..." />}
                {loading && <Spinner label="กำลังโหลด..." />}

                {data && !loading && (
                    <>
                        {googleSync
                            ? <MessageBar messageBarType={googleSync.ok ? MessageBarType.info : MessageBarType.error}>
                                Google Ads ดึงล่าสุด {formatDateTime(googleSync.time)} ช่วง {formatDay(googleSync.from)} – {formatDay(googleSync.to)}: {googleSync.ok ? 'สำเร็จ' : 'ไม่สำเร็จ'} ({googleSync.message})
                            </MessageBar>
                            : <MessageBar messageBarType={MessageBarType.warning}>ยังไม่เคยดึงข้อมูล Google Ads</MessageBar>}

                        {byPlatform.length > 0 && (
                            <Stack horizontal wrap tokens={{ childrenGap: 16 }}>
                                {byPlatform.map(([platform, cost]) => (
                                    <Stack key={platform} styles={statStyles}>
                                        <Text styles={mutedText}>{platformLabel(platform)} (บาท)</Text>
                                        <Text variant="xxLarge" styles={{ root: { fontWeight: 600 } }}>{formatMoney(cost)}</Text>
                                    </Stack>
                                ))}
                            </Stack>
                        )}

                        <Stack styles={sectionStyles} tokens={{ childrenGap: 10 }}>
                            <Stack horizontal horizontalAlign="space-between" verticalAlign="end" wrap tokens={{ childrenGap: 10 }}>
                                <Text variant="large" styles={{ root: { fontWeight: 600 } }}>รายการ ({formatNumber(spend.length)})</Text>
                                <Dropdown label="แพลตฟอร์ม" selectedKey={platformFilter} styles={{ root: { width: 240 } }}
                                    onChange={(event, option) => setPlatformFilter(option.key)}
                                    options={[{ key: 'all', text: 'ทุกแพลตฟอร์ม' }, ...byPlatform.map(([platform]) => ({ key: platform, text: platformLabel(platform) }))]} />
                            </Stack>
                            {spend.length > 0
                                ? <DetailsList items={spend} columns={columns} getKey={(item) => item.id}
                                    selectionMode={SelectionMode.none} layoutMode={DetailsListLayoutMode.fixedColumns} />
                                : <Text styles={mutedText}>ไม่มีค่าโฆษณาในช่วงนี้</Text>}
                        </Stack>

                        {(contentCost.length > 0 || contentCampaigns.length > 0) && (
                            <Stack styles={sectionStyles} tokens={{ childrenGap: 10 }}>
                                <Text variant="large" styles={{ root: { fontWeight: 600 } }}>
                                    ต้นทุนผลิต content ({formatNumber(contentCost.length)}) รวม {formatMoney(contentCost.reduce((sum, item) => sum + (Number(item.cost) || 0), 0))} บาท
                                </Text>
                                <Text styles={mutedText}>ไม่นับรวมในค่าโฆษณาและ ROAS ของโฆษณา ดูต้นทุนต่อ lead ของ content ในแท็บสรุปรายวัน</Text>
                                {contentCost.length > 0
                                    ? <DetailsList items={contentCost} columns={contentColumns} getKey={(item) => item.id}
                                        selectionMode={SelectionMode.none} layoutMode={DetailsListLayoutMode.fixedColumns} />
                                    : <Text styles={mutedText}>ไม่มีต้นทุน content ในช่วงนี้</Text>}
                            </Stack>
                        )}
                    </>
                )}
            </Stack>

            <Panel
                isOpen={Boolean(entry)}
                onDismiss={() => !busy && setEntry(null)}
                type={PanelType.medium}
                headerText="กรอกค่าโฆษณา"
                closeButtonAriaLabel="ปิด"
                isFooterAtBottom={true}
                onRenderFooterContent={() => (
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <PrimaryButton text={busy ? 'กำลังบันทึก...' : 'บันทึก'} onClick={saveEntry}
                            disabled={busy || !entry || !entry.date || entry.cost === '' || (!entry.campaignId && !entry.platformCampaignId.trim())} />
                        <DefaultButton text="ยกเลิก" disabled={busy} onClick={() => setEntry(null)} />
                    </Stack>
                )}
            >
                {entry && (
                    <Stack tokens={{ childrenGap: 12 }}>
                        {entryError && <MessageBar messageBarType={MessageBarType.error}>{entryError}</MessageBar>}
                        <Dropdown label="แพลตฟอร์ม" required selectedKey={entry.platform} options={MANUAL_PLATFORM_OPTIONS}
                            onChange={(event, option) => setEntry({ ...entry, platform: option.key, campaignId: '' })} />
                        <DatePicker label="วันที่" isRequired value={entry.date} maxDate={today} formatDate={formatDate}
                            onSelectDate={(date) => setEntry({ ...entry, date })} />
                        <Dropdown label="แคมเปญในทะเบียน" selectedKey={entry.campaignId} options={entryCampaignOptions}
                            onChange={(event, option) => setEntry({ ...entry, campaignId: option.key })} />
                        <TextField label="campaign ID ในแพลตฟอร์ม" value={entry.platformCampaignId}
                            description="ใส่เมื่อไม่ได้เลือกแคมเปญในทะเบียน หรือเพื่อแยกหลาย campaign ที่อยู่ในแคมเปญเดียวกัน"
                            onChange={(event, value) => setEntry({ ...entry, platformCampaignId: value })} />
                        <TextField label="ชื่อ campaign ในแพลตฟอร์ม" value={entry.platformCampaignName} maxLength={200}
                            onChange={(event, value) => setEntry({ ...entry, platformCampaignName: value })} />
                        <TextField label="ค่าใช้จ่าย (บาท)" required type="number" min={0} value={entry.cost}
                            onChange={(event, value) => setEntry({ ...entry, cost: value })} />
                        <Stack horizontal tokens={{ childrenGap: 10 }}>
                            <TextField label="impressions" type="number" min={0} value={entry.impressions} styles={{ root: { flex: 1 } }}
                                onChange={(event, value) => setEntry({ ...entry, impressions: value })} />
                            <TextField label="clicks" type="number" min={0} value={entry.clicks} styles={{ root: { flex: 1 } }}
                                onChange={(event, value) => setEntry({ ...entry, clicks: value })} />
                        </Stack>
                    </Stack>
                )}
            </Panel>

            <Panel
                isOpen={Boolean(contentEntry)}
                onDismiss={() => !busy && setContentEntry(null)}
                type={PanelType.medium}
                headerText="กรอกต้นทุนผลิต content"
                closeButtonAriaLabel="ปิด"
                isFooterAtBottom={true}
                onRenderFooterContent={() => (
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <PrimaryButton text={busy ? 'กำลังบันทึก...' : 'บันทึก'} onClick={saveContentCost}
                            disabled={busy || !contentEntry || !contentEntry.campaignId || !contentEntry.date || contentEntry.cost === ''} />
                        <DefaultButton text="ยกเลิก" disabled={busy} onClick={() => setContentEntry(null)} />
                    </Stack>
                )}
            >
                {contentEntry && (
                    <Stack tokens={{ childrenGap: 12 }}>
                        {contentError && <MessageBar messageBarType={MessageBarType.error}>{contentError}</MessageBar>}
                        <Text styles={mutedText}>ค่าผลิต / จ้างทำ content ที่ไม่ใช่ค่าโฆษณา กรอกวันและแคมเปญเดิมซ้ำ = เขียนทับ</Text>
                        <Dropdown label="แคมเปญ content" required selectedKey={contentEntry.campaignId}
                            options={contentCampaigns.map((item) => ({ key: item.id, text: item.name }))}
                            onChange={(event, option) => setContentEntry({ ...contentEntry, campaignId: option.key })} />
                        <DatePicker label="วันที่จ่าย / ปล่อย content" isRequired value={contentEntry.date} maxDate={today} formatDate={formatDate}
                            onSelectDate={(date) => setContentEntry({ ...contentEntry, date })} />
                        <TextField label="ต้นทุน (บาท)" required type="number" min={0} value={contentEntry.cost}
                            onChange={(event, value) => setContentEntry({ ...contentEntry, cost: value })} />
                        <TextField label="หมายเหตุ" multiline rows={3} value={contentEntry.note} maxLength={500}
                            onChange={(event, value) => setContentEntry({ ...contentEntry, note: value })} />
                    </Stack>
                )}
            </Panel>

            <Dialog
                hidden={!csv}
                onDismiss={() => !busy && setCsv(null)}
                dialogContentProps={{ type: DialogType.normal, title: 'นำเข้าค่าโฆษณาจาก CSV' }}
                minWidth={480}
            >
                {csv && (
                    <Stack tokens={{ childrenGap: 10 }}>
                        <Text>{csv.name}: {formatNumber(csv.items.length)} รายการ รวม {formatMoney(csvTotal)} บาท</Text>
                        <Text styles={mutedText}>คอลัมน์: {CSV_HEADER.join(', ')} (Google Ads ดึงจาก API ไม่ต้องนำเข้า)</Text>
                        {csv.errors.length > 0 && (
                            <MessageBar messageBarType={MessageBarType.error} isMultiline={true}>
                                {csv.errors.slice(0, 5).map((text) => <div key={text}>{text}</div>)}
                                {csv.errors.length > 5 && <div>และอีก {csv.errors.length - 5} รายการ</div>}
                            </MessageBar>
                        )}
                    </Stack>
                )}
                <DialogFooter>
                    <PrimaryButton text={busy ? 'กำลังนำเข้า...' : 'นำเข้า'} onClick={saveCsv}
                        disabled={busy || !csv || csv.items.length === 0 || csv.errors.length > 0} />
                    <DefaultButton text="ยกเลิก" disabled={busy} onClick={() => setCsv(null)} />
                </DialogFooter>
            </Dialog>

            <Dialog
                hidden={!sync}
                onDismiss={() => !busy && setSync(null)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'ดึง Google Ads ย้อนหลัง',
                    subText: `ดึงค่าใช้จ่ายและ click ได้ไม่เกิน ${SYNC_MAX_DAYS} วัน ถึงเมื่อวาน ข้อมูลวันเดิมจะถูกเขียนทับ`,
                }}
                minWidth={420}
            >
                {sync && (
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <DatePicker label="ตั้งแต่" value={sync.from} maxDate={sync.to} minDate={daysBefore(yesterday, SYNC_MAX_DAYS - 1)} formatDate={formatDate}
                            onSelectDate={(date) => date && setSync({ ...sync, from: date })} styles={{ root: { flex: 1 } }} />
                        <DatePicker label="ถึง" value={sync.to} minDate={sync.from} maxDate={yesterday} formatDate={formatDate}
                            onSelectDate={(date) => date && setSync({ ...sync, to: date })} styles={{ root: { flex: 1 } }} />
                    </Stack>
                )}
                <DialogFooter>
                    <PrimaryButton text={busy ? 'กำลังดึง...' : 'ดึงข้อมูล'} onClick={runSync}
                        disabled={busy || !sync || syncDays < 1 || syncDays > SYNC_MAX_DAYS} />
                    <DefaultButton text="ยกเลิก" disabled={busy} onClick={() => setSync(null)} />
                </DialogFooter>
            </Dialog>
        </div>
    );
};

export default Spend;
